import { useRef } from 'react';
import { Input, useColorMode } from '@chakra-ui/react';
import { useFinanceUI } from './ui';
import { Field } from './components';

/**
 * The time field — the one way these screens ask for a clock time.
 *
 * Speaks 'HH:mm' strings both ways, which is what every schedule, request body
 * and API already uses. The control underneath is the browser's own
 * `<input type="time">` — the CRM has no time picker of its own in general use,
 * and the native one is the right tool here — but it is wrapped for the same
 * two reasons `DateField` wraps the date picker: one place for the styling,
 * and one place for the fix below.
 *
 * ★ THE CLOCK ICON WAS BLACK ON DARK SCREENS. The browser draws a native
 * control's glyphs in its own colour scheme, and with nothing telling it
 * otherwise that scheme is "light" — a near-black clock on our dark input. No
 * amount of `color` fixes it, because the glyph is not text. The CSS property
 * that governs it is `color-scheme`, set here from the app's real colour mode
 * (Chakra's `useColorMode`, the same source the theme toggle writes). Dark
 * mode gets a light glyph; light mode is untouched.
 *
 * Two shapes, chosen by whether `label` is passed — exactly as DateField:
 *   <TimeField label='Send from' value={x} onChange={fn} />     -> wrapped in <Field>
 *   <TimeField ariaLabel='Send from' value={x} onChange={fn} /> -> bare (inline pairs)
 *
 * Never pass `label` AND wrap this in your own <Field> — that nests two labels.
 */

// Chakra v1 / React 17 have no useId; a module counter is enough — the id only
// has to be unique within a rendered page, and it must be stable across renders.
let seq = 0;

export function TimeField({
	value,
	onChange,

	label,
	ariaLabel,

	// forwarded to <Field> (label mode only)
	isRequired,
	error,
	help,
	tooltip,

	size = 'sm',
	isDisabled = false,

	maxW,
	w,
	...rest
}) {
	const ui = useFinanceUI();
	const { colorMode } = useColorMode();
	const idRef = useRef(null);
	if (idRef.current === null) {
		seq += 1;
		idRef.current = `fc-time-${seq}`;
	}
	const id = idRef.current;

	const input = (
		<Input
			id={id}
			type='time'
			size={size}
			value={value ?? ''}
			onChange={(e) => onChange(e.target.value)}
			isDisabled={isDisabled}
			aria-label={ariaLabel || label}
			bg={ui.inputBg}
			color={ui.textPrimary}
			borderColor={error ? 'red.500' : ui.border}
			_hover={{ borderColor: error ? 'red.500' : ui.borderStrong }}
			_focus={{
				borderColor: error ? 'red.500' : ui.focusRing,
				boxShadow: `0 0 0 1px ${error ? 'var(--chakra-colors-red-500)' : ui.focusRing}`,
			}}
			maxW={maxW}
			w={w}
			// Inline rather than `sx`, so it reaches the element as the literal
			// CSS property the browser reads for its native glyphs.
			style={{ colorScheme: colorMode === 'dark' ? 'dark' : 'light' }}
			{...rest}
		/>
	);

	if (!label) return input;

	return (
		<Field label={label} isRequired={isRequired} error={error} help={help} tooltip={tooltip} id={id}>
			{input}
		</Field>
	);
}

export default TimeField;
